import { FormattedMessage, useIntl } from 'react-intl';
import { FilterPanel } from '@/components/FilterPanel.tsx';
import Input from '@/components/core/Input.tsx';
import { Dropdown, OptionsType } from '@/components/core/Dropdown.tsx';
import Button from '@/components/core/Button.tsx';
import { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import { keepPreviousData, useQueries } from '@tanstack/react-query';
import { Loading } from '@/components/Loading.tsx';
import { VirtualizedList } from '@/components/VirtualizedList.tsx';
import { StudentListItem } from '@/components/Students/StudentListItem.tsx';
import { useAscOrDescIcon } from '@/hooks/useSort.tsx';
import { RightPanel } from '@/components/RightPanel/RightPanel.tsx';
import { Route as StudentTravelRoute } from '@/routes/$tenantId/_layout/students/travels/$view';
import { Route as StudentTransportationRoute } from '@/routes/$tenantId/_layout/students/transportation/$view';
import { Route as StudentYouthCardRoute } from '@/routes/$tenantId/_layout/students/child-and-youth/$view';
import { createSortConfig } from '@/utils/sort';
import { useSearchFilterSort } from '@/hooks/useSearchFilterSort';
import { FilterType, SearchFilterParams } from '@/types/filterSearch';
import { FilteredListContainer } from '../FilteredListContainer';
import { useExportStudentsToExcel } from '@/hooks/useExcelExport';
import { IconExportExcel } from '@/icons';
import { toast } from '@/hooks/useToast';
import {
  useGetCollectionTicketClaims,
  useListCollectionTicketClaimMemberStatesHook,
  useTravellerGlobalSearch,
  getListCollectionTicketClaimMemberStatesQueryKey,
} from '@/api/trafikportalen';
import { toCardSearchQuery } from '@/helpers/cardSerial';
import { getMatchingTravellerSearchCards } from '@/helpers/travellerSearch';
import { getPeriodStatus } from '@/helpers/periodStatus';
import { CLAIM_STATES, SCHEDULED_LABEL } from '@/helpers/claimStates';
import { getMembershipStatus } from '@/helpers/membershipStatus';
import { scheduledClaimCount } from '@/helpers/scheduledClaims';
import type { FormattedStudent, StudentType } from '@/types/students';
import type { CollectionTicketClaimMemberStateDTO } from '@/api/trafikportalen';

const SCHEDULED_FILTER_ID = 'scheduled';

type RouteType =
  | typeof StudentTravelRoute
  | typeof StudentTransportationRoute
  | typeof StudentYouthCardRoute;

interface StudentListProps {
  students: FormattedStudent[];
  isLoading?: boolean;
  routeType: RouteType;
  studentType: StudentType;
  collectionId?: string;
}

export function StudentList({
  students,
  isLoading,
  routeType,
  studentType,
  collectionId,
}: StudentListProps) {
  const {
    query,
    sort,
    direction,
    status: filterStatus,
    manuallyModified: filterManuallyModified,
    claimState: filterClaimState,
  } = routeType.useSearch() as SearchFilterParams;
  const [announcement, setAnnouncement] = useState('');
  const sortSnapshot = useRef<{ sort?: string; direction?: string } | null>(
    null,
  );

  const isChildAndYouth = studentType === 'child-and-youth';

  const sortConfigs = useMemo(() => {
    const textFields: (keyof FormattedStudent)[] = [
      'first_name',
      'last_name',
      'national_identity_number',
    ];
    if (!isChildAndYouth) {
      textFields.push('school_name');
    }
    return createSortConfig<FormattedStudent>({ textFields });
  }, [isChildAndYouth]);

  const searchKeys = useMemo(() => {
    const keys: (keyof FormattedStudent)[] = [
      'name',
      'national_identity_number',
      'guardian1_email',
      'guardian2_email',
      'bobcat_private_email',
    ];
    if (!isChildAndYouth) {
      keys.splice(2, 0, 'school_name');
    }
    return keys;
  }, [isChildAndYouth]);

  const {
    filterSearch,
    sortedItems,
    setFilterSearch,
    handleSort,
    handleSearchParams,
    handleSearch,
  } = useSearchFilterSort<FormattedStudent>({
    items: students || [],
    searchKeys,
    sortConfigs,
    routePath: routeType.fullPath,
    defaultSearchParams: {
      sort: 'first_name',
      direction: 'asc',
      status: [],
      manuallyModified: [],
      claimState: [],
    },
  });

  const cardSearchQuery = toCardSearchQuery(filterSearch?.query ?? '');

  const { data: cardSearchResults } = useTravellerGlobalSearch(
    { q: cardSearchQuery ?? '' },
    {
      query: {
        enabled: cardSearchQuery !== undefined,
        placeholderData: keepPreviousData,
      },
    },
  );

  const cardMatchedStudents = useMemo((): FormattedStudent[] => {
    if (!cardSearchQuery || !cardSearchResults?.length) {
      return [];
    }

    const studentByTravellerId = new Map(
      (students || []).map((student) => [student.traveller_id, student]),
    );

    const matches: FormattedStudent[] = [];
    const seen = new Set<string>();

    for (const result of cardSearchResults) {
      if (
        getMatchingTravellerSearchCards(result, cardSearchQuery).length === 0
      ) {
        continue;
      }

      const student = studentByTravellerId.get(result.traveller.id);
      if (!student || seen.has(student.id)) {
        continue;
      }

      seen.add(student.id);
      matches.push(student);
    }

    return matches;
  }, [cardSearchQuery, cardSearchResults, students]);

  const searchedStudents = useMemo(() => {
    if (cardMatchedStudents.length === 0) {
      return sortedItems;
    }

    const existingIds = new Set(sortedItems.map((student) => student.id));
    const extras = cardMatchedStudents.filter(
      (student) => !existingIds.has(student.id),
    );

    if (extras.length === 0) {
      return sortedItems;
    }

    const combined = [...sortedItems, ...extras];
    const sortFn = sortConfigs[filterSearch?.sort as keyof typeof sortConfigs];

    if (!sortFn) {
      return combined;
    }

    return combined.sort((a, b) =>
      filterSearch?.direction === 'desc' ? -1 * sortFn(a, b) : sortFn(a, b),
    );
  }, [
    cardMatchedStudents,
    filterSearch?.direction,
    filterSearch?.sort,
    sortConfigs,
    sortedItems,
  ]);

  const { formatMessage } = useIntl();
  const getIcon = useAscOrDescIcon();
  const exportStudents = useExportStudentsToExcel();

  const { data: ticketClaims } = useGetCollectionTicketClaims(
    collectionId ?? '',
    { query: { enabled: !!collectionId } },
  );

  const activeClaimIds = useMemo(() => {
    if (!ticketClaims) return [];
    return ticketClaims
      .filter((claim) => getPeriodStatus(claim) === 'active')
      .map((claim) => claim.id);
  }, [ticketClaims]);

  const activeClaimCount = activeClaimIds.length;

  const listMemberStates = useListCollectionTicketClaimMemberStatesHook();

  const memberStateQueries = useQueries({
    queries: activeClaimIds.map((claimId) => ({
      queryKey: getListCollectionTicketClaimMemberStatesQueryKey(claimId),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        listMemberStates(claimId, signal),
      enabled: !!claimId,
    })),
  });

  const memberStateMap = useMemo(() => {
    const map = new Map<string, CollectionTicketClaimMemberStateDTO[]>();

    for (const query of memberStateQueries) {
      if (!query.data) continue;
      for (const state of query.data) {
        const existing = map.get(state.collection_member_id) ?? [];
        existing.push(state);
        map.set(state.collection_member_id, existing);
      }
    }

    return map;
  }, [memberStateQueries]);

  const statusOptions = useMemo(() => {
    return [
      {
        id: 'active',
        label: formatMessage({ defaultMessage: 'Aktiv' }),
      },
      {
        id: 'pending-inactivation',
        label: formatMessage({
          defaultMessage: 'Inaktivering schemalagd',
        }),
      },
      {
        id: 'inactive',
        label: formatMessage({ defaultMessage: 'Inaktiv' }),
      },
    ];
  }, [formatMessage]);

  const manuallyModifiedOptions = useMemo(() => {
    return [
      {
        id: 'modified',
        label: formatMessage({ defaultMessage: 'Manuellt ändrad' }),
      },
      {
        id: 'not_modified',
        label: formatMessage({ defaultMessage: 'Ej manuellt ändrad' }),
      },
    ];
  }, [formatMessage]);

  const claimStateOptions = useMemo(() => {
    const options: OptionsType[] = [
      {
        id: SCHEDULED_FILTER_ID,
        label: formatMessage(SCHEDULED_LABEL),
      },
    ];
    for (const [stateId, config] of Object.entries(CLAIM_STATES)) {
      options.push({
        id: stateId,
        label: formatMessage(config.label),
      });
    }
    return options;
  }, [formatMessage]);

  const filteredStudents = useMemo(() => {
    let result = searchedStudents;

    if (filterSearch?.status && filterSearch.status.length > 0) {
      const statusIds = filterSearch.status.map((s) => s.id);
      result = result.filter((student) => {
        const membershipStatus = getMembershipStatus(student);
        const studentStatus =
          membershipStatus === 'inactive' ||
          membershipStatus === 'pending-inactivation'
            ? membershipStatus
            : 'active';
        return statusIds.includes(studentStatus);
      });
    }

    if (
      filterSearch?.manuallyModified &&
      filterSearch.manuallyModified.length > 0
    ) {
      const modifiedIds = filterSearch.manuallyModified.map((m) => m.id);
      result = result.filter((student) => {
        const modifiedStatus = student.manually_modified
          ? 'modified'
          : 'not_modified';
        return modifiedIds.includes(modifiedStatus);
      });
    }

    if (filterSearch?.claimState && filterSearch.claimState.length > 0) {
      const selectedIds = new Set(filterSearch.claimState.map((c) => c.id));
      const wantsScheduled = selectedIds.has(SCHEDULED_FILTER_ID);
      result = result.filter((student) => {
        const states = memberStateMap.get(student.id) ?? [];
        const hasScheduled =
          scheduledClaimCount(activeClaimCount, states.length, student) > 0;
        if (wantsScheduled && hasScheduled) return true;
        return states.some((s) => selectedIds.has(s.state));
      });
    }

    return result;
  }, [
    searchedStudents,
    filterSearch?.status,
    filterSearch?.manuallyModified,
    filterSearch?.claimState,
    memberStateMap,
    activeClaimCount,
  ]);

  const handleFilter = useCallback(
    (
      filter: OptionsType[],
      filterType: 'status' | 'manuallyModified' | 'claimState',
    ) => {
      setFilterSearch((prev) => ({
        ...prev!,
        [filterType]: filter as FilterType[],
      }));

      handleSearchParams({
        [filterType]: filter.map((f) => f.id),
      });
    },
    [handleSearchParams, setFilterSearch],
  );

  const handleExport = useCallback(() => {
    if (!filteredStudents.length) return;

    try {
      exportStudents(filteredStudents, studentType);
    } catch {
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Ett fel uppstod vid export av elever',
          }),
        },
        { autoClose: 3000 },
      );
    }
  }, [exportStudents, filteredStudents, studentType, formatMessage]);

  const renderStudentItem = useCallback(
    (student: FormattedStudent) => {
      return (
        <StudentListItem
          key={student.id}
          student={student}
          studentType={studentType}
          claimStates={memberStateMap.get(student.id)}
          activeClaimCount={activeClaimCount}
        />
      );
    },
    [studentType, memberStateMap, activeClaimCount],
  );

  const sortControlName = useCallback(
    (field: string, fieldLabel: string) => {
      if (!filterSearch || filterSearch.sort !== field) {
        return formatMessage(
          { defaultMessage: 'Sortera efter {field}' },
          { field: fieldLabel },
        );
      }

      const direction =
        filterSearch.direction === 'asc'
          ? formatMessage({ defaultMessage: 'stigande' })
          : formatMessage({ defaultMessage: 'fallande' });

      return formatMessage(
        {
          defaultMessage:
            'Sortera efter {field}, {direction}. Aktivera för att vända ordningen.',
        },
        { field: fieldLabel, direction },
      );
    },
    [filterSearch, formatMessage],
  );

  useEffect(() => {
    if (!filterSearch) return;

    const timeout = window.setTimeout(() => {
      const count = filteredStudents.length;
      const filterParts: string[] = [];
      const query = filterSearch.query?.trim();

      if (query) {
        filterParts.push(
          formatMessage({ defaultMessage: 'sökning {query}' }, { query }),
        );
      }
      if (filterSearch.status?.length) {
        filterParts.push(
          filterSearch.status.map((item) => item.label).join(', '),
        );
      }
      if (filterSearch.manuallyModified?.length) {
        filterParts.push(
          filterSearch.manuallyModified.map((item) => item.label).join(', '),
        );
      }
      if (filterSearch.claimState?.length) {
        filterParts.push(
          filterSearch.claimState.map((item) => item.label).join(', '),
        );
      }

      let message = formatMessage(
        {
          defaultMessage: '{count, plural, one {# elev} other {# elever}}',
        },
        { count },
      );

      if (filterParts.length > 0) {
        message = formatMessage(
          { defaultMessage: '{summary}, filtrerat på {filters}' },
          { summary: message, filters: filterParts.join(', ') },
        );
      }

      const previousSort = sortSnapshot.current;
      const sortChanged =
        previousSort !== null &&
        (previousSort.sort !== filterSearch.sort ||
          previousSort.direction !== filterSearch.direction);

      if (sortChanged) {
        const sortLabelMap: Record<string, string> = {
          first_name: formatMessage({ defaultMessage: 'förnamn' }),
          last_name: formatMessage({ defaultMessage: 'efternamn' }),
          national_identity_number: formatMessage({
            defaultMessage: 'personnummer',
          }),
          ...(!isChildAndYouth
            ? { school_name: formatMessage({ defaultMessage: 'skola' }) }
            : {}),
        };
        const sortLabel =
          sortLabelMap[filterSearch.sort ?? ''] ??
          formatMessage({ defaultMessage: 'förnamn' });
        const directionLabel =
          filterSearch.direction === 'asc'
            ? formatMessage({ defaultMessage: 'stigande' })
            : formatMessage({ defaultMessage: 'fallande' });

        message = formatMessage(
          {
            defaultMessage:
              '{summary}, sorterade efter {sortLabel} {directionLabel}',
          },
          { summary: message, sortLabel, directionLabel },
        );
      }

      sortSnapshot.current = {
        sort: filterSearch.sort,
        direction: filterSearch.direction,
      };
      setAnnouncement((current) => (current === message ? current : message));
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [filterSearch, filteredStudents, formatMessage, isChildAndYouth]);

  useEffect(() => {
    if (!isChildAndYouth || !filterSearch) return;
    if (filterSearch.sort !== 'school_name') return;

    setFilterSearch((prev) => (prev ? { ...prev, sort: 'first_name' } : null));
    handleSearchParams({
      sort: 'first_name',
      direction: filterSearch.direction ?? 'asc',
    });
  }, [isChildAndYouth, filterSearch, setFilterSearch, handleSearchParams]);

  useEffect(() => {
    if (filterSearch) return;

    const statusIds = filterStatus || [];
    const manuallyModifiedIds = filterManuallyModified || [];
    const claimStateIds = filterClaimState || [];

    const statuses = statusIds
      .map((id) => statusOptions.find((s) => s.id === id))
      .filter(Boolean) as FilterType[];

    const manuallyModified = manuallyModifiedIds
      .map((id) => manuallyModifiedOptions.find((m) => m.id === id))
      .filter(Boolean) as FilterType[];

    const claimState = claimStateIds
      .map((id) => claimStateOptions.find((c) => c.id === id))
      .filter(Boolean) as FilterType[];

    setFilterSearch({
      query: query ?? '',
      status: statuses,
      manuallyModified,
      claimState,
      sort: sort ?? 'first_name',
      direction: direction ?? 'asc',
    });
  }, [
    direction,
    filterStatus,
    filterManuallyModified,
    filterClaimState,
    filterSearch,
    statusOptions,
    manuallyModifiedOptions,
    claimStateOptions,
    setFilterSearch,
    query,
    sort,
  ]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <FilteredListContainer>
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {announcement}
      </div>
      <RightPanel>
        {filterSearch && (
          <>
            <FilterPanel
              firstInput={
                <Input
                  type="text"
                  label={formatMessage({ defaultMessage: 'Sök' })}
                  placeholder={formatMessage(
                    isChildAndYouth
                      ? {
                          id: 'page.students.list.search.placeholder.childAndYouth',
                          defaultMessage:
                            'Sök på namn, personnummer, vårdnadshavares e-post eller kortnummer',
                        }
                      : {
                          id: 'page.students.list.search.placeholder',
                          defaultMessage:
                            'Sök på namn, personnummer, skola eller kortnummer',
                        },
                  )}
                  id="studentData-search"
                  value={filterSearch.query}
                  onChange={(e) => handleSearch(e.target.value)}
                  aria-description={
                    isChildAndYouth
                      ? formatMessage({
                          defaultMessage:
                            'Sök efter elever genom att skriva namn, personnummer, vårdnadshavares e-post eller kortnummer',
                        })
                      : formatMessage({
                          defaultMessage:
                            'Sök efter elever genom att skriva namn, personnummer, skola eller kortnummer',
                        })
                  }
                />
              }
              sortingOptions={[
                <Button
                  key="student-first-name"
                  variant={
                    filterSearch.sort === 'first_name' ? 'filled' : 'outline'
                  }
                  startIcon={getIcon({
                    selectedSort: filterSearch.sort!,
                    wantedSort: 'first_name',
                    sortDirection: filterSearch.direction!,
                  })}
                  onClick={() => handleSort('first_name')}
                  aria-label={sortControlName(
                    'first_name',
                    formatMessage({ defaultMessage: 'Förnamn' }),
                  )}
                  aria-pressed={filterSearch.sort === 'first_name'}
                >
                  <FormattedMessage defaultMessage="Förnamn" />
                </Button>,
                <Button
                  key="student-last-name"
                  variant={
                    filterSearch.sort === 'last_name' ? 'filled' : 'outline'
                  }
                  startIcon={getIcon({
                    selectedSort: filterSearch.sort!,
                    wantedSort: 'last_name',
                    sortDirection: filterSearch.direction!,
                  })}
                  onClick={() => handleSort('last_name')}
                  aria-label={sortControlName(
                    'last_name',
                    formatMessage({ defaultMessage: 'Efternamn' }),
                  )}
                  aria-pressed={filterSearch.sort === 'last_name'}
                >
                  <FormattedMessage defaultMessage="Efternamn" />
                </Button>,
                <Button
                  key="student-social-security-number"
                  variant={
                    filterSearch.sort === 'national_identity_number'
                      ? 'filled'
                      : 'outline'
                  }
                  startIcon={getIcon({
                    selectedSort: filterSearch.sort!,
                    wantedSort: 'national_identity_number',
                    sortDirection: filterSearch.direction!,
                  })}
                  onClick={() => handleSort('national_identity_number')}
                  aria-label={sortControlName(
                    'national_identity_number',
                    formatMessage({ defaultMessage: 'Personnummer' }),
                  )}
                  aria-pressed={
                    filterSearch.sort === 'national_identity_number'
                  }
                >
                  <FormattedMessage defaultMessage="Personnummer" />
                </Button>,
                ...(isChildAndYouth
                  ? []
                  : [
                      <Button
                        key="student-school-name"
                        variant={
                          filterSearch.sort === 'school_name'
                            ? 'filled'
                            : 'outline'
                        }
                        startIcon={getIcon({
                          selectedSort: filterSearch.sort!,
                          wantedSort: 'school_name',
                          sortDirection: filterSearch.direction!,
                        })}
                        onClick={() => handleSort('school_name')}
                        aria-label={sortControlName(
                          'school_name',
                          formatMessage({ defaultMessage: 'Skola' }),
                        )}
                        aria-pressed={filterSearch.sort === 'school_name'}
                      >
                        <FormattedMessage defaultMessage="Skola" />
                      </Button>,
                    ]),
              ]}
              filterOptions={[
                <Dropdown
                  key="student-status"
                  label={formatMessage({ defaultMessage: 'Status' })}
                  id="status-selection"
                  placeholder={formatMessage({ defaultMessage: 'Alla' })}
                  value={filterSearch?.status}
                  options={statusOptions}
                  onValueChange={(value) => handleFilter(value, 'status')}
                  aria-description={formatMessage({
                    defaultMessage: 'Filtrera elever genom att välja status',
                  })}
                  multiSelect
                />,
                <Dropdown
                  key="student-manually-modified"
                  label={formatMessage({
                    defaultMessage: 'Manuellt ändrad',
                  })}
                  id="manually-modified-selection"
                  placeholder={formatMessage({ defaultMessage: 'Alla' })}
                  value={filterSearch?.manuallyModified}
                  options={manuallyModifiedOptions}
                  onValueChange={(value) =>
                    handleFilter(value, 'manuallyModified')
                  }
                  aria-description={formatMessage({
                    defaultMessage:
                      'Filtrera elever efter om de ändrats manuellt',
                  })}
                  multiSelect
                />,
                ...(activeClaimCount > 0
                  ? [
                      <Dropdown
                        key="student-claim-state"
                        label={formatMessage({
                          defaultMessage: 'Biljettstatus',
                        })}
                        id="claim-state-selection"
                        placeholder={formatMessage({ defaultMessage: 'Alla' })}
                        value={filterSearch?.claimState}
                        options={claimStateOptions}
                        onValueChange={(value) =>
                          handleFilter(value, 'claimState')
                        }
                        aria-description={formatMessage({
                          defaultMessage:
                            'Filtrera elever efter biljettstatus i aktiva perioder',
                        })}
                        multiSelect
                      />,
                    ]
                  : []),
              ]}
            />
            <Button
              onClick={handleExport}
              disabled={filteredStudents.length === 0}
              className="mt-2 w-full"
              startIcon={<IconExportExcel />}
            >
              <FormattedMessage defaultMessage="Exportera till Excel" />
            </Button>
          </>
        )}
      </RightPanel>
      <VirtualizedList
        count={filteredStudents.length}
        countName={formatMessage({ defaultMessage: 'elever' })}
        ariaLabel={formatMessage({ defaultMessage: 'Elever' })}
        totalItems={students}
        filteredItems={filteredStudents}
        renderItem={renderStudentItem}
        getItemKey={(student) => student.id}
      />
    </FilteredListContainer>
  );
}
